import React, { useState, useEffect, useRef } from 'react';
import { ParadiseOpsLogo, IconScan, IconInventory, IconOrders, IconFarmers, IconVoice, IconUsers } from '../components/Icons';

const T = '#12CFC4';
const C = '#F97316';
const IND = '#818CF8';
const BG = '#070F1E';
const BG2 = '#0C1828';
const TEXT = '#EEF4FF';
const MUTED = '#6E9EC0';
const DIM = '#B8CEE0';
const BORDER = 'rgba(18,207,196,0.13)';
const FC = ['#12CFC4', '#60A5FA', '#A78BFA', '#F97316', '#34D399', '#F472B6'];

const FEATURES = [
  { Icon: IconScan,      name: 'Invoice Scanning',   desc: 'Snap a photo of any invoice. AI extracts every line item, quantity, and price in seconds — no manual entry, no transcription errors.',          tag: 'PDF · HEIC · JPEG' },
  { Icon: IconInventory, name: 'Live Inventory',      desc: 'Every ingredient tracked in real time. Invoices update counts automatically as they land; staff log usage on the go from any device.',         tag: 'Low-stock alerts' },
  { Icon: IconOrders,    name: 'Order Approvals',     desc: 'Purchase requests route to the right person with full context — who asked, what it costs, whether budget exists. Approve in one tap.',         tag: 'Full audit trail' },
  { Icon: IconFarmers,   name: 'Farmer & Vendor SMS', desc: 'Order directly from local suppliers by text. No portals, no phone tag — a plain conversation that automatically creates a purchase record.',   tag: 'Twilio-powered' },
  { Icon: IconVoice,     name: 'Voice Interface',     desc: 'Hands full? Ask out loud. "How much ahi do we have?" gets a real answer. Built for the pace of a working kitchen, not a conference room.',     tag: 'Natural language' },
  { Icon: IconUsers,     name: 'Multi-Location',       desc: 'One admin account. Every location — restaurants and properties. Each team sees only their data. Onboard a new location in minutes.',          tag: 'Unlimited locations' },
];

const CAPABILITIES = [
  { category: 'Roles & Access',  items: ['Chef', 'General Manager', 'Bartender', 'Supplies', 'Restaurant Admin', 'Super Admin'] },
  { category: 'Integrations',    items: ['Toast POS', 'Twilio SMS', 'Google Cloud Vision', 'Weather-based forecasting'] },
  { category: 'Security',        items: ['JWT authentication', 'bcrypt-hashed passwords', 'Row-level security (RLS)', 'TLS encryption in transit'] },
  { category: 'Platform',        items: ['Web-based — no install', 'Mobile-optimized', 'Subdomain per restaurant', 'AI voice & text interface'] },
];

const FAQS = [
  { q: 'How does onboarding work?',
    a: "We set up your restaurant's subdomain, create your admin account, and walk your team through the platform. Most restaurants are fully operational within a day. We handle all technical configuration." },
  { q: 'What POS systems do you integrate with?',
    a: "We integrate natively with Toast POS for sales data and menu sync. Additional integrations are in development — reach out if you're on a different system and we'll tell you where it stands." },
  { q: 'Is my data secure and isolated from other restaurants?',
    a: "Yes. Each restaurant's data is isolated at the database level using row-level security policies. Passwords are bcrypt-hashed with cost factor 12. All data is TLS-encrypted in transit and at rest on SOC 2 certified infrastructure." },
  { q: 'How many locations can I manage?',
    a: 'As many as you need. Each location gets its own subdomain and its own staff portal. Your super-admin account has full visibility across all of them from one login.' },
  { q: 'Can I control which staff see which features?',
    a: 'Yes. Each role has a defined permission set — chefs see inventory and invoices, GMs see approvals and reporting, bartenders see bar stock only. Permissions can be extended for custom requirements.' },
  { q: 'How is this different from a spreadsheet or existing software?',
    a: "Spreadsheets don't scan invoices, route approval requests, answer voice queries, or send purchase orders by SMS. They also can't alert you when stock is low or track who approved what and when. Paradise Ops does all of that, without an IT department or a six-month implementation." },
];

function CapIcon({ type }) {
  const s = { width: 32, height: 32 };
  if (type === 0) return (
    <svg {...s} viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="11" r="6" fill={`${T}20`} stroke={T} strokeWidth="1.5"/>
      <path d="M4 28c0-6.627 5.373-12 12-12s12 5.373 12 12" stroke={T} strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
  if (type === 1) return (
    <svg {...s} viewBox="0 0 32 32" fill="none">
      <circle cx="11" cy="16" r="8" fill={`${T}15`} stroke={T} strokeWidth="1.5"/>
      <circle cx="21" cy="16" r="8" fill={`${T}15`} stroke={T} strokeWidth="1.5"/>
    </svg>
  );
  if (type === 2) return (
    <svg {...s} viewBox="0 0 32 32" fill="none">
      <path d="M16 2L2 8v9c0 7 5.6 13.6 14 15 8.4-1.4 14-8 14-15V8L16 2z" fill={`${T}15`} stroke={T} strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M11 16l4 4 6-7" stroke={T} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
  return (
    <svg {...s} viewBox="0 0 32 32" fill="none">
      <rect x="2" y="6" width="28" height="20" rx="3" fill={`${T}15`} stroke={T} strokeWidth="1.5"/>
      <path d="M2 12h28" stroke={T} strokeWidth="1.5"/>
      <circle cx="7" cy="9" r="1.5" fill={T}/>
      <circle cx="11" cy="9" r="1.5" fill={T}/>
    </svg>
  );
}

function DashMockup() {
  const rows = [
    { name: 'Ahi Tuna',     qty: '28', unit: 'lbs',  status: 'OK',   sc: '#12CFC4' },
    { name: 'Jasmine Rice', qty: '12', unit: 'bags', status: 'OK',   sc: '#12CFC4' },
    { name: 'Oyster Sauce', qty: '2',  unit: 'gal',  status: 'LOW',  sc: '#F97316' },
    { name: 'Baby Arugula', qty: '8',  unit: 'lbs',  status: 'OK',   sc: '#12CFC4' },
    { name: 'Wagyu Ribeye', qty: '1',  unit: 'pcs',  status: 'CRIT', sc: '#f87171' },
  ];
  const pw = { OK: 22, LOW: 24, CRIT: 30 };
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'absolute', top: '30%', left: '50%', transform: 'translate(-50%,-50%)', width: 420, height: 320, borderRadius: '50%', background: `radial-gradient(circle, rgba(18,207,196,0.22) 0%, transparent 70%)`, filter: 'blur(55px)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '5%', right: '5%', width: 220, height: 220, borderRadius: '50%', background: `radial-gradient(circle, rgba(249,115,22,0.16) 0%, transparent 70%)`, filter: 'blur(45px)', pointerEvents: 'none' }} />
      <svg viewBox="0 0 520 340" style={{ width: '100%', maxWidth: 520, filter: 'drop-shadow(0 32px 72px rgba(4,10,22,0.85))', display: 'block' }} aria-hidden="true">
        <rect width="520" height="340" rx="12" fill="#07101e" stroke="rgba(18,207,196,0.28)" strokeWidth="1"/>
        <rect width="520" height="38" rx="12" fill="#0a1628"/>
        <rect y="26" width="520" height="12" fill="#0a1628"/>
        <circle cx="18" cy="19" r="5" fill="#FF5F57"/>
        <circle cx="34" cy="19" r="5" fill="#FEBC2E"/>
        <circle cx="50" cy="19" r="5" fill="#28C840"/>
        <text x="260" y="23" textAnchor="middle" fill="#2d4d6a" fontSize="10" fontFamily="system-ui,sans-serif">Paradise Ops — Inventory</text>
        <rect x="0" y="38" width="62" height="302" fill="#060d1a"/>
        <rect x="0" y="98" width="3" height="20" rx="1.5" fill="#12CFC4"/>
        {[58, 84, 110, 136, 162, 188].map((y, i) => (
          <g key={i}>
            <rect x="16" y={y} width="30" height="20" rx="5" fill={i === 1 ? 'rgba(18,207,196,0.2)' : 'rgba(255,255,255,0.04)'}/>
            <rect x="22" y={y + 7} width="18" height="2.5" rx="1.5" fill={i === 1 ? '#12CFC4' : 'rgba(255,255,255,0.18)'}/>
            {i !== 1 && <rect x="22" y={y + 12} width="13" height="2.5" rx="1.5" fill="rgba(255,255,255,0.09)"/>}
          </g>
        ))}
        {/* Stat cards */}
        <rect x="74" y="50" width="130" height="68" rx="8" fill="#0d1b2e" stroke="rgba(255,255,255,0.04)" strokeWidth="1"/>
        <text x="86" y="69" fill="#30688a" fontSize="8" fontFamily="system-ui" fontWeight="700">ACTIVE ITEMS</text>
        <text x="86" y="92" fill="#EEF4FF" fontSize="26" fontFamily="system-ui" fontWeight="800">142</text>
        <rect x="86" y="106" width="70" height="3" rx="1.5" fill="rgba(255,255,255,0.06)"/>
        <rect x="86" y="106" width="50" height="3" rx="1.5" fill="#12CFC4"/>
        <rect x="214" y="50" width="130" height="68" rx="8" fill="#0d1b2e" stroke="rgba(255,255,255,0.04)" strokeWidth="1"/>
        <text x="226" y="69" fill="#30688a" fontSize="8" fontFamily="system-ui" fontWeight="700">LOW STOCK</text>
        <text x="226" y="92" fill="#F97316" fontSize="26" fontFamily="system-ui" fontWeight="800">3</text>
        <text x="226" y="108" fill="#F97316" fontSize="9" fontFamily="system-ui">needs attention</text>
        <rect x="354" y="50" width="150" height="68" rx="8" fill="#0d1b2e" stroke="rgba(255,255,255,0.04)" strokeWidth="1"/>
        <text x="366" y="69" fill="#30688a" fontSize="8" fontFamily="system-ui" fontWeight="700">PENDING ORDERS</text>
        <text x="366" y="92" fill="#EEF4FF" fontSize="26" fontFamily="system-ui" fontWeight="800">7</text>
        <text x="366" y="108" fill="#12CFC4" fontSize="9" fontFamily="system-ui">2 ready to approve</text>
        {/* Table */}
        <rect x="74" y="132" width="432" height="24" rx="4" fill="rgba(255,255,255,0.025)"/>
        <text x="86" y="148" fill="#2c4d6a" fontSize="8" fontFamily="system-ui" fontWeight="700">ITEM</text>
        <text x="302" y="148" fill="#2c4d6a" fontSize="8" fontFamily="system-ui" fontWeight="700">QTY</text>
        <text x="360" y="148" fill="#2c4d6a" fontSize="8" fontFamily="system-ui" fontWeight="700">UNIT</text>
        <text x="432" y="148" fill="#2c4d6a" fontSize="8" fontFamily="system-ui" fontWeight="700">STATUS</text>
        {rows.map(({ name, qty, unit, status, sc }, i) => (
          <g key={i}>
            {i % 2 === 0 && <rect x="74" y={160 + i * 29} width="432" height="29" fill="rgba(255,255,255,0.012)"/>}
            <circle cx="84" cy={160 + i * 29 + 14} r="4" fill={sc} opacity="0.9"/>
            <text x="95" y={160 + i * 29 + 19} fill="#C0D2E4" fontSize="11" fontFamily="system-ui">{name}</text>
            <text x="302" y={160 + i * 29 + 19} fill="#C0D2E4" fontSize="11" fontFamily="system-ui" fontWeight="600">{qty}</text>
            <text x="360" y={160 + i * 29 + 19} fill="#2c4d6a" fontSize="11" fontFamily="system-ui">{unit}</text>
            <rect x="428" y={160 + i * 29 + 7} width={pw[status] || 22} height="16" rx="4" fill={`${sc}22`} stroke={`${sc}44`} strokeWidth="0.8"/>
            <text x="432" y={160 + i * 29 + 18} fill={sc} fontSize="8" fontFamily="system-ui" fontWeight="700">{status}</text>
          </g>
        ))}
        <defs>
          <linearGradient id="dashFade" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#07101e" stopOpacity="0"/>
            <stop offset="100%" stopColor="#07101e" stopOpacity="0.92"/>
          </linearGradient>
        </defs>
        <rect x="62" y="262" width="458" height="78" fill="url(#dashFade)"/>
      </svg>
    </div>
  );
}

function useReveal() {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } }, { threshold: 0.08 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}

function Reveal({ children, delay = 0, style = {} }) {
  const [ref, vis] = useReveal();
  return (
    <div ref={ref} style={{ transition: `opacity 0.65s ${delay}ms ease, transform 0.65s ${delay}ms ease`, opacity: vis ? 1 : 0, transform: vis ? 'none' : 'translateY(24px)', ...style }}>
      {children}
    </div>
  );
}

export default function Landing() {
  const [openFaq, setOpenFaq] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 55);
    window.addEventListener('scroll', onScroll, { passive: true });
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      window.removeEventListener('scroll', onScroll);
      document.documentElement.style.scrollBehavior = '';
    };
  }, []);

  return (
    <div style={{ background: BG, color: DIM, fontFamily: 'system-ui,-apple-system,"Segoe UI",sans-serif', lineHeight: 1.6 }}>
      <style>{`
        .po * { box-sizing: border-box; }
        .po a { text-decoration: none; }
        .po-nav-a { color: ${MUTED}; font-size: 11px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; transition: color 0.2s; }
        .po-nav-a:hover { color: ${T}; }
        .po-cta { background: ${T}; color: #050e1a; padding: 10px 24px; border-radius: 7px; font-size: 12px; font-weight: 800; letter-spacing: 0.06em; transition: opacity 0.18s, transform 0.15s; display: inline-block; }
        .po-cta:hover { opacity: 0.88; transform: translateY(-1px); }
        .po-faq-btn { background: none; border: none; cursor: pointer; width: 100%; text-align: left; color: inherit; font-family: inherit; font-size: 16px; padding: 24px 0; display: flex; justify-content: space-between; align-items: center; gap: 24px; }
        .po-feat-row { display: grid; grid-template-columns: 280px 1fr; gap: 0; padding: 40px 0; border-bottom: 1px solid ${BORDER}; align-items: start; }
        .po-cap-grid { display: grid; grid-template-columns: repeat(4, 1fr); }
        .po-about-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 96px; align-items: start; }
        .po-nav-links { display: flex; align-items: center; gap: 40px; }
        .po-hero-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center; }
        .po-stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); }
        @media (max-width: 1024px) {
          .po-hero-cols { grid-template-columns: 1fr; }
          .po-hero-right { display: none; }
        }
        @media (max-width: 900px) {
          .po-feat-row { grid-template-columns: 1fr; gap: 14px; }
          .po-cap-grid { grid-template-columns: repeat(2, 1fr); }
          .po-about-cols { grid-template-columns: 1fr; gap: 48px; }
          .po-nav-links { gap: 20px; }
        }
        @media (max-width: 640px) {
          .po-cap-grid { grid-template-columns: 1fr; }
          .po-stats-grid { grid-template-columns: 1fr; }
          .po-nav-links .po-nav-a { display: none; }
          .po-section-pad { padding-left: 24px !important; padding-right: 24px !important; }
          nav.po-nav { padding: 0 24px !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          * { transition-duration: 0ms !important; animation-duration: 0ms !important; }
        }
      `}</style>

      {/* NAV */}
      <nav className="po po-nav" style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 52px', height: 68,
        background: scrolled ? 'rgba(7,15,30,0.94)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: `1px solid ${scrolled ? BORDER : 'transparent'}`,
        transition: 'background 0.3s, border-color 0.3s',
      }}>
        <ParadiseOpsLogo size={40} />
        <div className="po-nav-links">
          {['features', 'capabilities', 'about', 'faq'].map(id => (
            <a key={id} href={`#${id}`} className="po-nav-a">{id}</a>
          ))}
          <a href="mailto:hello@paradiseops.ai" className="po-cta">Get in touch</a>
        </div>
      </nav>

      {/* HERO */}
      <section style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', position: 'relative', overflow: 'hidden', paddingTop: 68 }}>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <div style={{ position: 'absolute', top: '-15%', left: '-8%', width: 750, height: 750, borderRadius: '50%', background: `radial-gradient(circle, rgba(18,207,196,0.14) 0%, transparent 65%)`, filter: 'blur(70px)' }} />
          <div style={{ position: 'absolute', bottom: '-5%', right: '-8%', width: 550, height: 550, borderRadius: '50%', background: `radial-gradient(circle, rgba(249,115,22,0.11) 0%, transparent 65%)`, filter: 'blur(70px)' }} />
          <div style={{ position: 'absolute', top: '35%', right: '28%', width: 350, height: 350, borderRadius: '50%', background: `radial-gradient(circle, rgba(129,140,248,0.10) 0%, transparent 65%)`, filter: 'blur(60px)' }} />
          <div style={{ position: 'absolute', inset: 0, backgroundImage: `linear-gradient(rgba(18,207,196,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(18,207,196,0.05) 1px, transparent 1px)`, backgroundSize: '72px 72px' }} />
          <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse 110% 70% at 50% 50%, transparent 40%, ${BG} 100%)` }} />
        </div>

        <div className="po po-section-pad" style={{ maxWidth: 1240, margin: '0 auto', padding: '80px 52px', position: 'relative', zIndex: 1, width: '100%' }}>
          <div className="po-hero-cols">
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, border: `1px solid ${BORDER}`, borderRadius: 24, padding: '6px 18px', marginBottom: 40, background: 'rgba(18,207,196,0.05)' }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: T, boxShadow: `0 0 10px ${T}`, flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: T }}>Operations Intelligence Platform</span>
              </div>
              <h1 style={{ fontSize: 'clamp(40px, 5.5vw, 80px)', fontWeight: 800, lineHeight: 1.0, letterSpacing: '-0.03em', color: TEXT, marginBottom: 28, textWrap: 'balance' }}>
                Your business,{' '}
                <span style={{ background: `linear-gradient(130deg, ${T} 0%, ${IND} 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  running like
                </span>{' '}
                it always should.
              </h1>
              <p style={{ fontSize: 'clamp(15px, 1.8vw, 18px)', color: MUTED, lineHeight: 1.75, maxWidth: 520, marginBottom: 24 }}>
                Every spreadsheet, sticky note, and gut-feel decision — replaced by something that actually thinks ahead.
              </p>
              {/* Vertical pills */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 40, flexWrap: 'wrap' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, border: `1px solid rgba(18,207,196,0.28)`, borderRadius: 8, padding: '8px 16px', background: 'rgba(18,207,196,0.07)', fontSize: 13, fontWeight: 700, color: T }}>
                  🍽️ Restaurant Ops
                </div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, border: `1px solid rgba(129,140,248,0.28)`, borderRadius: 8, padding: '8px 16px', background: 'rgba(129,140,248,0.07)', fontSize: 13, fontWeight: 700, color: IND }}>
                  🏢 Property Management
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 52 }}>
                {FEATURES.map(({ name }, i) => (
                  <span key={name} style={{ border: `1px solid ${FC[i]}35`, borderRadius: 20, padding: '6px 16px', fontSize: 12, color: FC[i], fontWeight: 600, background: `${FC[i]}12` }}>{name}</span>
                ))}
              </div>
              <a href="#features" style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 10, color: MUTED, textDecoration: 'none', fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, transition: 'color 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.color = T}
                onMouseLeave={e => e.currentTarget.style.color = MUTED}
              >
                <span>Explore the platform</span>
                <svg width="18" height="28" viewBox="0 0 18 28" fill="none">
                  <line x1="9" y1="0" x2="9" y2="22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <polyline points="3,16 9,22 15,16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
            </div>
            <div className="po-hero-right">
              <DashMockup />
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <div style={{ borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, background: `linear-gradient(90deg, rgba(18,207,196,0.04) 0%, rgba(129,140,248,0.04) 50%, rgba(249,115,22,0.04) 100%)` }}>
        <div className="po po-section-pad po-stats-grid" style={{ maxWidth: 1120, margin: '0 auto', padding: '0 52px' }}>
          {[
            { val: '< 5s', label: 'per invoice scanned', color: T },
            { val: '6',    label: 'role types, one platform', color: IND },
            { val: '∞',   label: 'locations, one login', color: C },
          ].map(({ val, label, color }, i) => (
            <div key={i} style={{ padding: '38px 0', textAlign: 'center', borderRight: i < 2 ? `1px solid ${BORDER}` : 'none' }}>
              <div style={{ fontSize: 'clamp(36px, 4vw, 56px)', fontWeight: 800, color, letterSpacing: '-0.04em', lineHeight: 1 }}>{val}</div>
              <div style={{ fontSize: 13, color: MUTED, marginTop: 10, fontWeight: 500 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* VERTICALS */}
      <section style={{ padding: '100px 0', background: BG, borderBottom: `1px solid ${BORDER}` }}>
        <div className="po po-section-pad" style={{ maxWidth: 1120, margin: '0 auto', padding: '0 52px' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 56 }}>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: T, marginBottom: 14 }}>Two platforms</p>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 50px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.025em', color: TEXT, marginBottom: 16 }}>
                One login. Every operation.
              </h2>
              <p style={{ fontSize: 16, color: MUTED, maxWidth: 520, margin: '0 auto' }}>
                Switch verticals from the sidebar. The same AI, the same intelligence layer — purpose-built for each business type.
              </p>
            </div>
          </Reveal>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            {/* Restaurant vertical */}
            <Reveal delay={0}>
              <div style={{ border: `1.5px solid rgba(18,207,196,0.25)`, borderRadius: 16, padding: '40px 36px', background: 'linear-gradient(135deg, rgba(18,207,196,0.07) 0%, rgba(18,207,196,0.02) 100%)', height: '100%' }}>
                <div style={{ fontSize: 36, marginBottom: 16 }}>🍽️</div>
                <h3 style={{ fontSize: 22, fontWeight: 800, color: TEXT, marginBottom: 8, letterSpacing: '-0.02em' }}>Restaurant Ops</h3>
                <p style={{ fontSize: 14, color: MUTED, marginBottom: 28, lineHeight: 1.65 }}>
                  Full back-of-house intelligence — from the moment an invoice lands to the moment a dish goes out.
                </p>
                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 13 }}>
                  {[
                    ['📷', 'AI invoice scanning & line-item extraction'],
                    ['📦', 'Live inventory with par-level alerts'],
                    ['✅', 'Order approvals with full audit trail'],
                    ['💬', 'Vendor & farmer SMS ordering'],
                    ['🎙️', 'Voice interface — ask anything, hands-free'],
                    ['🌤️', 'Weather-based covers & reorder forecasting'],
                  ].map(([icon, text]) => (
                    <li key={text} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, fontSize: 14, color: '#b8ccde' }}>
                      <span style={{ fontSize: 15, flexShrink: 0, marginTop: 1 }}>{icon}</span>
                      {text}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            {/* Property vertical */}
            <Reveal delay={80}>
              <div style={{ border: `1.5px solid rgba(129,140,248,0.25)`, borderRadius: 16, padding: '40px 36px', background: 'linear-gradient(135deg, rgba(129,140,248,0.07) 0%, rgba(129,140,248,0.02) 100%)', height: '100%' }}>
                <div style={{ fontSize: 36, marginBottom: 16 }}>🏢</div>
                <h3 style={{ fontSize: 22, fontWeight: 800, color: TEXT, marginBottom: 8, letterSpacing: '-0.02em' }}>Property Management</h3>
                <p style={{ fontSize: 14, color: MUTED, marginBottom: 28, lineHeight: 1.65 }}>
                  Short-term rentals and properties — occupancy, maintenance, turnovers, and owner statements in one place.
                </p>
                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 13 }}>
                  {[
                    ['🏠', 'Multi-property & unit management'],
                    ['📋', 'Turnover scheduling with tap-to-check checklists'],
                    ['🔧', 'Work orders with priority, assignment & cost tracking'],
                    ['💰', 'Owner statements with automatic management fee calc'],
                    ['📊', 'Live occupancy dashboard across all units'],
                    ['🔄', 'Checkout-to-check-in turnover calendar'],
                  ].map(([icon, text]) => (
                    <li key={text} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, fontSize: 14, color: '#b8ccde' }}>
                      <span style={{ fontSize: 15, flexShrink: 0, marginTop: 1 }}>{icon}</span>
                      {text}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
          <Reveal delay={160}>
            <div style={{ marginTop: 20, textAlign: 'center', padding: '20px 0', color: MUTED, fontSize: 13 }}>
              <span style={{ border: `1px solid ${BORDER}`, borderRadius: 8, padding: '8px 20px', display: 'inline-block' }}>
                Both verticals run on the same account — toggle between them from the sidebar with a single click
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" style={{ padding: '120px 0', background: BG2 }}>
        <div className="po po-section-pad" style={{ maxWidth: 1120, margin: '0 auto', padding: '0 52px' }}>
          <Reveal>
            <div style={{ marginBottom: 64 }}>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: T, marginBottom: 14 }}>What we do</p>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 50px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.025em', color: TEXT, maxWidth: 560 }}>
                Six things that run a restaurant better.
              </h2>
            </div>
          </Reveal>
          <div style={{ borderTop: `1px solid ${BORDER}` }}>
            {FEATURES.map(({ Icon, name, desc, tag }, i) => (
              <Reveal key={name} delay={i * 60}>
                <div className="po-feat-row">
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, paddingRight: 52 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: `${FC[i]}18`, border: `1px solid ${FC[i]}38`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2, boxShadow: `0 0 24px ${FC[i]}20` }}>
                      <Icon width={18} height={18} stroke={FC[i]} strokeWidth={1.8} />
                    </div>
                    <span style={{ fontSize: 16, fontWeight: 700, color: TEXT, lineHeight: 1.3, paddingTop: 12 }}>{name}</span>
                  </div>
                  <div>
                    <p style={{ fontSize: 15, color: MUTED, lineHeight: 1.75, marginBottom: 12 }}>{desc}</p>
                    <span style={{ display: 'inline-block', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: FC[i], border: `1px solid ${FC[i]}38`, borderRadius: 4, padding: '3px 9px', background: `${FC[i]}0e` }}>{tag}</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section id="capabilities" style={{ padding: '120px 0', background: BG, borderTop: `1px solid ${BORDER}` }}>
        <div className="po po-section-pad" style={{ maxWidth: 1120, margin: '0 auto', padding: '0 52px' }}>
          <Reveal>
            <div style={{ marginBottom: 64 }}>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: T, marginBottom: 14 }}>Under the hood</p>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 50px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.025em', color: TEXT }}>
                The right tools, built in.
              </h2>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="po-cap-grid" style={{ border: `1px solid ${BORDER}`, borderRadius: 12, overflow: 'hidden' }}>
              {CAPABILITIES.map(({ category, items }, ci) => (
                <div key={category} style={{ padding: '40px 32px', borderRight: ci < 3 ? `1px solid ${BORDER}` : 'none', background: ci % 2 === 0 ? 'rgba(18,207,196,0.025)' : 'rgba(249,115,22,0.018)' }}>
                  <div style={{ marginBottom: 16 }}><CapIcon type={ci} /></div>
                  <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: T, marginBottom: 20 }}>{category}</p>
                  <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {items.map(item => (
                      <li key={item} style={{ fontSize: 14, color: '#b8ccde', display: 'flex', alignItems: 'center', gap: 9 }}>
                        <span style={{ width: 4, height: 4, borderRadius: '50%', background: T, flexShrink: 0, opacity: 0.7 }} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" style={{ padding: '120px 0', background: BG2, borderTop: `1px solid ${BORDER}` }}>
        <div className="po po-section-pad" style={{ maxWidth: 1120, margin: '0 auto', padding: '0 52px' }}>
          <Reveal>
            <div className="po-about-cols">
              <div>
                <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: T, marginBottom: 14 }}>About</p>
                <h2 style={{ fontSize: 'clamp(28px, 4vw, 50px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.025em', color: TEXT, marginBottom: 32 }}>
                  Built for operators.<br />Not accountants.
                </h2>
                <div style={{ width: 40, height: 3, background: T, borderRadius: 2, marginBottom: 32 }} />
                <p style={{ fontSize: 16, color: MUTED, lineHeight: 1.85, marginBottom: 20 }}>
                  Most operations software is built by people who have never run a Saturday dinner service or turned over a vacation rental between guests. It shows. Paradise Ops was designed around how real operators actually work — fast, physical, and short on spare time.
                </p>
                <p style={{ fontSize: 16, color: MUTED, lineHeight: 1.85 }}>
                  One platform for your restaurant and your properties. Same login, same intelligence layer, same AI — purpose-built for each vertical. Not another spreadsheet. Not a system that requires a consultant to configure.
                </p>
              </div>
              <div style={{ paddingTop: 48 }}>
                <blockquote style={{ borderLeft: `3px solid ${T}`, paddingLeft: 28, fontSize: 'clamp(18px, 2.5vw, 24px)', fontStyle: 'italic', fontWeight: 300, color: '#c4d4e4', lineHeight: 1.65, marginBottom: 28 }}>
                  "Spreadsheets were never meant to run your operations. We were."
                </blockquote>
                <div style={{ paddingLeft: 28, marginBottom: 52 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: TEXT }}>Paradise Ops</p>
                  <p style={{ fontSize: 12, color: MUTED, marginTop: 3 }}>Operations Intelligence Platform</p>
                </div>
                <div style={{ paddingLeft: 28, display: 'flex', gap: 40 }}>
                  {[{ n: '6', label: 'Core modules', accent: T }, { n: '6', label: 'Staff roles', accent: IND }, { n: '∞', label: 'Locations', accent: C }].map(({ n, label, accent }) => (
                    <div key={label}>
                      <div style={{ fontSize: 44, fontWeight: 800, color: accent, lineHeight: 1, letterSpacing: '-0.03em' }}>{n}</div>
                      <div style={{ fontSize: 12, color: MUTED, marginTop: 4, fontWeight: 500 }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" style={{ padding: '120px 0', background: BG, borderTop: `1px solid ${BORDER}` }}>
        <div className="po po-section-pad" style={{ maxWidth: 1120, margin: '0 auto', padding: '0 52px' }}>
          <Reveal>
            <div style={{ marginBottom: 64 }}>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: T, marginBottom: 14 }}>FAQ</p>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 50px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.025em', color: TEXT }}>Common questions.</h2>
            </div>
            <div style={{ borderTop: `1px solid ${BORDER}` }}>
              {FAQS.map(({ q, a }, i) => (
                <div key={i} style={{ borderBottom: `1px solid ${BORDER}` }}>
                  <button className="po-faq-btn" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                    <span style={{ fontSize: 16, fontWeight: 600, color: TEXT, lineHeight: 1.45 }}>{q}</span>
                    <span style={{ fontSize: 22, color: T, flexShrink: 0, display: 'inline-block', transform: openFaq === i ? 'rotate(45deg)' : 'none', transition: 'transform 0.25s ease' }}>+</span>
                  </button>
                  {openFaq === i && (
                    <p style={{ paddingBottom: 24, paddingRight: '10%', fontSize: 15, color: MUTED, lineHeight: 1.8 }}>{a}</p>
                  )}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ padding: '44px 0', background: '#040c18', borderTop: `1px solid ${BORDER}` }}>
        <div className="po po-section-pad" style={{ maxWidth: 1120, margin: '0 auto', padding: '0 52px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20 }}>
          <ParadiseOpsLogo size={36} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28 }}>
            {[['Features', '#features'], ['Capabilities', '#capabilities'], ['About', '#about'], ['FAQ', '#faq'], ['Contact', 'mailto:hello@paradiseops.ai']].map(([label, href]) => (
              <a key={label} href={href} className="po-nav-a">{label}</a>
            ))}
          </div>
          <p style={{ fontSize: 12, color: '#2c4560' }}>© {new Date().getFullYear()} Paradise Ops</p>
        </div>
      </footer>
    </div>
  );
}
